import React from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {HowSearchWorks} from 'components/HowSearchWorks/HowSearchWorks'
import {SortByDesktop} from 'components/SortBy'
import {TotalPriceToggle} from 'components/TotalPriceToggle/TotalPriceToggle'
import {
  getAnchorCity,
  getIsInitialSearch,
  getIsSearchHasResults,
  getIsSearchPending,
  getSearchType
} from 'modules/sapiSearch/selectors'
import {SearchType} from 'modules/sapiSearch/utils'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {cssTheme} from '@vio/atlas/themes'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {PointsPriceToggle} from '@vio/shared/src/whiteLabel/PointsPriceToggle'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s300};
  /* To match spacing with hotel cards, that have 2px border */
  border-right: ${cssTheme.layout.spacing.s200} solid transparent;
  border-left: ${cssTheme.layout.spacing.s200} solid transparent;
`

// A border rather than a Divider, so it shows only when both toggles render
const Toggles = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s400};
  margin-inline-start: auto;

  &:empty {
    display: none;
  }

  > * + * {
    padding-inline-start: ${cssTheme.layout.spacing.s400};
    border-inline-start: 1px solid ${cssTheme.colors.border.neutral.secondary};
  }
`

const MobileWrapper = styled.div`
  &.--shows-anchor-city-name {
    padding: ${cssTheme.layout.spacing.s950} ${cssTheme.layout.spacing.s100} 0;
  }
`

const MobileToggles = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s300};
  margin-bottom: ${cssTheme.layout.spacing.s500};

  &:empty {
    margin: 0;
  }
`

export const SearchResultsControls = () => {
  const {isMobile} = useDeviceLayout()
  const {search} = useBrandCapabilities()
  const isSearchHasResults = useSelector(getIsSearchHasResults)
  const isLoading = useSelector(getIsSearchPending)
  const isInitialSearch = useSelector(getIsInitialSearch)
  const searchType = useSelector(getSearchType)
  const anchorCityName = useSelector(getAnchorCity)

  const isSearchLoading = isLoading && !isSearchHasResults && isInitialSearch

  const showsAnchorCityName =
    search.showsAnchorCityName &&
    (searchType === SearchType.Place || searchType === SearchType.IATA) &&
    Boolean(anchorCityName)

  if (isMobile) {
    return (
      <MobileWrapper
        className={cx(showsAnchorCityName && '--shows-anchor-city-name')}
      >
        <MobileToggles>
          <TotalPriceToggle />
          <PointsPriceToggle />
        </MobileToggles>
        <HowSearchWorks />
      </MobileWrapper>
    )
  }

  if (isSearchLoading)
    return (
      <Wrapper>
        <SkeletonLoader width={110} />
        <SkeletonLoader width={160} marginTop={0} />
      </Wrapper>
    )

  return (
    <Wrapper>
      {search.hasSort && <SortByDesktop />}
      <HowSearchWorks />
      <Toggles>
        <PointsPriceToggle />
        <TotalPriceToggle />
      </Toggles>
    </Wrapper>
  )
}
